import { useMsal } from "@azure/msal-react";
import { useLocation, useParams } from "react-router-dom";
import { isLegalNoticePath } from "@/components/legal/legalNotices";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useIsTrainerDashboard } from "@/hooks/useIsTrainerDashboard";

/**
 * Decides which header controls fit the current route and sign-in state.
 * Used by the header identity block and the header toolbar.
 */
const useHeaderRoute = () => {
  const { courseTitle, courseData, loading, progressPercent } =
    useCourseDetailStore();
  const { accounts } = useMsal();
  const { pathname } = useLocation();
  const { enrollmentId, courseExternalId } = useParams<{
    enrollmentId?: string;
    courseExternalId?: string;
  }>();
  const isPreview = !!courseExternalId;
  const isTrainerPreview = pathname.includes("/trainer-preview");
  const isDashboard = pathname.endsWith("/dashboard");
  const isTrainerDashboard = useIsTrainerDashboard();
  const isCourseRoute =
    pathname.startsWith("/course") || pathname.startsWith("/preview");
  const isLegalNoticePage = isLegalNoticePath(pathname);
  const isCourse = isCourseRoute && !isDashboard && !isLegalNoticePage;
  const showProgress =
    isCourse && !isTrainerPreview && !!courseData && progressPercent > 0;
  const showFlashcardsButton =
    !!enrollmentId &&
    (isCourse || isDashboard) &&
    !isPreview &&
    !isTrainerPreview &&
    !isTrainerDashboard;
  // Course documents are available wherever an enrollment is in context
  // (participant dashboard, trainer dashboard, and in-course view).
  const showAttachmentsButton =
    !!enrollmentId && !isPreview && !isLegalNoticePage;
  const isSignedIn = accounts.length > 0;
  // Opening Impressum while signed out must not reveal Hilfe.
  const showGuestImpressum =
    !isSignedIn && (pathname === "/" || isLegalNoticePage);
  const showHelp = !isTrainerDashboard && !showGuestImpressum;
  const showImpressum = showGuestImpressum;
  const isLoginPage = pathname === "/";
  const showDashboard =
    isSignedIn && !isPreview && !isLoginPage && !isTrainerDashboard;
  const showGlossary = isCourse;
  const showCourseTools =
    showHelp || showFlashcardsButton || showGlossary || showAttachmentsButton;
  const showHeaderTools = showCourseTools || showImpressum;
  const showHeaderDivider = showHeaderTools && isSignedIn;

  return {
    courseTitle,
    loading,
    progressPercent,
    enrollmentId,
    isPreview,
    isTrainerPreview,
    isDashboard,
    isTrainerDashboard,
    isCourse,
    showDashboard,
    showProgress,
    showFlashcardsButton,
    showAttachmentsButton,
    showHelp,
    showImpressum,
    showGlossary,
    showCourseTools,
    showHeaderDivider,
  };
};

export { useHeaderRoute };
